import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useFacebookPixel } from "@/hooks/useFacebookPixel";
import { Profile, DatabaseProfile } from "@/types/profile";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CreditCard, Gem, Gauge, Clock, CheckCircle2, AlertCircle, RefreshCw, FileText, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Account() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackPurchaseInitiated, trackPurchase } = useFacebookPixel();
  const [isLoading, setIsLoading] = useState(false);
  const [isDowngradeDialogOpen, setIsDowngradeDialogOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState<'pro' | 'free' | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  // Parse the URL params on component mount and when URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('checkout');
    
    if (status) {
      const plan = params.get('plan');
      setCheckoutStatus(status);
      
      if (status === 'success' && plan) {
        setIsProcessingCheckout(true);
        toast.success(t('subscribed_success', { plan: t(plan === 'pro' ? 'pro_plan' : 'free_plan') }));
        
        // Track successful purchase
        const planValue = plan === 'pro' ? 9.99 : 19.99;
        trackPurchase(planValue, 'USD');
        
        // Give the webhook some time to process, then refetch
        const timer = setTimeout(() => {
          refetchProfile().then(() => {
            setIsProcessingCheckout(false);
          });
        }, 3000);
        
        return () => clearTimeout(timer);
      } else if (status === 'canceled') {
        toast.info(t('checkout_canceled'));
      }
      
      // Clean up URL parameters
      navigate('/account', { replace: true });
    }
  }, [navigate, trackPurchase]);

  const { 
    data: profile, 
    isLoading: isProfileLoading, 
    refetch: refetchProfile 
  } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      // First, query the profiles table
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;
      
      // Then query the subscriptions table to get cancel_at_period_end
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      
      // No need to throw on subscription error, just log it
      if (subscriptionError) {
        console.error("Error fetching subscription:", subscriptionError);
      }
      
      const dbProfile = profileData as DatabaseProfile;
      
      const convertedProfile: Profile = {
        ...dbProfile,
        display_preferences: dbProfile.display_preferences ? 
          (dbProfile.display_preferences as unknown as Profile['display_preferences']) : 
          { theme: 'system', fontSize: 'normal', highContrast: false },
        notification_preferences: dbProfile.notification_preferences ?
          (dbProfile.notification_preferences as unknown as Profile['notification_preferences']) :
          { emailNotifications: true, inAppNotifications: true, collaborationAlerts: true },
        cancel_at_period_end: subscriptionData?.cancel_at != null
      };
      
      return convertedProfile;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!user && !isProfileLoading) {
      navigate("/");
    }
  }, [user, isProfileLoading, navigate]);

  const handleSubscribe = async (plan: 'pro') => {
    setIsLoading(true);
    setErrorMessage(null);
    
    // Track purchase initiation
    // const planValue = plan === 'pro' ? 9.99 : 19.99;
    // trackPurchaseInitiated(planValue, 'USD');
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan }
      });

      if (error) {
        console.error("Function error:", error);
        throw new Error(error.message || t('failed_checkout_start'));
      }
      
      if (!data?.success) {
        const errorMsg = data?.message || data?.error || "Failed to start checkout process";
        console.error(errorMsg);
        throw new Error(errorMsg || t('failed_checkout_start'));
      }
      
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(t('no_checkout_url'));
      }
    } catch (error: any) {
      console.error("Error starting checkout:", error);
      setErrorMessage(error.message || t('unknown_error'));
      toast.error(t('failed_checkout_start_with_reason', { reason: error.message || t('unknown_error') }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDowngradeDialog = (plan: 'free') => {
    setTargetPlan(plan);
    setIsDowngradeDialogOpen(true);
  };

  const handleDowngrade = async () => {
    if (!targetPlan) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const { data, error } = await supabase.functions.invoke("handle-downgrade", {
        body: { targetPlan }
      });

      if (error) {
        console.error("Function error:", error);
        throw new Error(error.message || t('failed_downgrade'));
      }
      
      if (!data?.success) {
        const errorMsg = data?.message || data?.error || "Failed to process downgrade";
        throw new Error(errorMsg || t('failed_downgrade'));
      }
      
      toast.success(data.message || t('downgrade_success'));
      setIsDowngradeDialogOpen(false);
      refetchProfile();
    } catch (error: any) {
      console.error("Error processing downgrade:", error);
      setErrorMessage(error.message || t('unknown_error'));
      toast.error(t('failed_downgrade_with_reason', { reason: error.message || t('unknown_error') }));
    } finally {
      setIsLoading(false);
    }
  };

  // Manual refresh function
  const handleManualRefresh = () => {
    setIsProcessingCheckout(true);
    refetchProfile().then(() => {
      setIsProcessingCheckout(false);
      toast.success(t('subscription_refreshed'));
    }).catch(error => {
      setIsProcessingCheckout(false);
      toast.error(t('failed_refresh_subscription'));
    });
  };

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTierDetails = (tier: string | null) => {
    switch (tier) {
      case "pro":
        return {
          name: "Pro",
          color: "bg-blue-500",
          projects: "Unlimited",
          storage: "5GB",
        };
      default:
        return {
          name: "Free",
          color: "bg-gray-500",
          projects: "1",
          storage: "1GB",
        };
    }
  };

  const tierDetails = getTierDetails(profile?.subscription_tier);
  
  const showFreeDowngradeOption = profile?.subscription_tier === 'pro';
  
  // Check if there's a pending downgrade to free
  const isPendingDowngradeToFree = 
    profile?.subscription_expires_at && 
    new Date(profile.subscription_expires_at) > new Date() && 
    profile?.subscription_tier !== 'free' &&
    !!profile.cancel_at_period_end;

  return (
    <div className="min-h-screen w-full site-gradient">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate("/")} 
              className="w-full sm:w-auto flex items-center justify-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back_to_dashboard')}
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-center sm:text-left w-full">
              {t('account_and_subscription')}
            </h1>
          </div>

          {errorMessage && (
            <Card className="border-red-300 bg-red-50 text-red-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  {t('error')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p>{errorMessage}</p>
                <p className="mt-2 text-sm">
                  {t('contact_support_if_issue_persists')}
                </p>
              </CardContent>
            </Card>
          )}

          {isProcessingCheckout && (
            <Card className="border-blue-300 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-blue-700">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                  <p>{t('processing_subscription_update')}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('current_subscription')}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={`${tierDetails.color} text-white`}>
                    {t(tierDetails.name === 'Pro' ? 'pro_plan' : 'free_plan')}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleManualRefresh}
                    disabled={isProcessingCheckout}
                    className="h-8 w-8"
                    title={t('refresh_subscription_info')}
                  >
                    <RefreshCw className={`h-4 w-4 ${isProcessingCheckout ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {t('current_plan_and_details')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Gauge className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('projects.title')}:</span>
                  <span className="font-medium">{tierDetails.projects}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {isPendingDowngradeToFree ? t('expiration_date') : t('renewal_date')}
                  </span>
                  <span className="font-medium">
                    {profile?.subscription_expires_at
                      ? formatDate(profile.subscription_expires_at)
                      : t('not_available')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('storage')}:</span>
                  <span className="font-medium">{tierDetails.storage}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                  <span className="text-muted-foreground">{t('status')}:</span>
                  <span className="font-medium">
                    {profile?.subscription_expires_at && 
                     new Date(profile.subscription_expires_at) > new Date() 
                     ? isPendingDowngradeToFree ? t('active_downgrading') : t('active') 
                     : t('inactive')}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('manage_subscription')}</CardTitle>
              <CardDescription>
                {t('choose_plan_for_needs')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6">
                <Card className="border-2 hover:border-blue-500 transition-all">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Gem className="h-5 w-5 text-blue-500" />
                      {t('pro_plan')}
                    </CardTitle>
                    <CardDescription>{t('pro_plan_description')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="text-2xl font-bold">$9.99<span className="text-sm font-normal text-muted-foreground">/{t('month')}</span></div>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>{t('unlimited_projects')}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>{t('five_gb_storage')}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span>{t('priority_support')}</span>
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    {profile?.subscription_tier === 'pro' ? (
                      <Button 
                        variant="outline"
                        disabled
                        className="w-full"
                      >
                        {t('current_plan')}
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => handleSubscribe('pro')}
                        disabled={isLoading || isProcessingCheckout}
                        className="w-full bg-blue-500 hover:bg-blue-600"
                      >
                        {isLoading ? (
                          <>
                            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                            {t('processing')}
                          </>
                        ) : (
                          t('upgrade_to_pro')
                        )}
                      </Button>
                    )}
                  </CardFooter>
                </Card>

                {showFreeDowngradeOption && (
                  <>
                    <Separator className="my-6" />
                    <div className="mt-4">
                      <Card className="border-2 hover:border-gray-500 transition-all">
                        <CardHeader>
                          <CardTitle>{t('free_plan')}</CardTitle>
                          <CardDescription>{t('free_plan_description')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="text-2xl font-bold">$0<span className="text-sm font-normal text-muted-foreground">/{t('month')}</span></div>
                          <ul className="space-y-2">
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span>{t('one_project')}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span>{t('one_gb_storage')}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span><FileText className="h-3.5 w-3.5 inline mr-1" /> {t('pdf_exports')}</span>
                            </li>
                          </ul>
                        </CardContent>
                        <CardFooter>
                          <Button 
                            variant="outline"
                            onClick={() => handleOpenDowngradeDialog('free')}
                            className="w-full border-gray-300"
                            disabled={isLoading || isProcessingCheckout || profile?.subscription_tier === 'free'}
                          >
                            {isLoading ? (
                              <>
                                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                                {t('processing')}
                              </>
                            ) : (
                              profile?.subscription_tier === 'free' ? t('current_plan') : t('downgrade_to_free')
                            )}
                          </Button>
                        </CardFooter>
                      </Card>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('account_information')}</CardTitle>
              <CardDescription>{t('your_account_details')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('email')}</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('name')}</p>
                  <p className="font-medium">{profile?.full_name || t('not_set')}</p>
                </div>
                {profile?.company && (
                  <div>
                    <p className="text-sm text-muted-foreground">{t('company')}</p>
                    <p className="font-medium">{profile.company}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={isDowngradeDialogOpen} onOpenChange={setIsDowngradeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('confirm_subscription_downgrade')}</DialogTitle>
            <DialogDescription>
              {t('confirm_downgrade_description')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="text-muted-foreground">
              {t('downgrade_effective_date', { date: formatDate(profile?.subscription_expires_at) })}
              <span className="block mt-2">
                {t('free_plan_limitations')}
              </span>
            </p>
          </div>
          
          <DialogFooter className="sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDowngradeDialogOpen(false)}
              disabled={isLoading}
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDowngrade}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  {t('processing')}
                </>
              ) : (
                t('confirm_downgrade')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
